import * as React from "react";
import { List } from "react-window";
import { BrowserRow } from "./BrowserRow";
import { ColumnRenderer } from "../types";
import styles from "../NGProviderBrowser.module.scss";

const ROW_HEIGHT = 40;

interface VirtualizedBodyProps {
  processedItemIds: string[];
  itemsMap: Map<string, any[]>;
  selectedIdsSet: Set<string>;
  focusedIndex: number;
  labels: string[];
  multipleChoice: boolean;
  rightAlign: string[];
  monoColumns: string[];
  columnRenderers: ColumnRenderer[];
  onSelect: (value: string) => void;
  onFocus: (index: number) => void;
  listRef: React.RefObject<any>;
  containerHeight: number;
  tableWidth?: number;
  gridTemplateColumns?: string;
  hasVerticalOverflow?: boolean;
  pinnedCount: number;
}

interface RowProps {
  processedItemIds: string[];
  itemsMap: Map<string, any[]>;
  selectedIdsSet: Set<string>;
  focusedIndex: number;
  labels: string[];
  multipleChoice: boolean;
  rightAlign: string[];
  monoColumns: string[];
  columnRenderers: ColumnRenderer[];
  onSelect: (value: string) => void;
  onFocus: (index: number) => void;
  gridTemplateColumns?: string;
  pinnedCount: number;
}

export const VirtualizedBody: React.FC<VirtualizedBodyProps> = ({
  processedItemIds,
  itemsMap,
  selectedIdsSet,
  focusedIndex,
  labels,
  multipleChoice,
  rightAlign,
  monoColumns,
  columnRenderers,
  onSelect,
  onFocus,
  listRef,
  containerHeight,
  tableWidth,
  gridTemplateColumns,
  hasVerticalOverflow = false,
  pinnedCount,
}) => {
  // Memoize the row props to prevent unnecessary re-renders
  const rowProps = React.useMemo<RowProps>(
    () => ({
      processedItemIds,
      itemsMap,
      selectedIdsSet,
      focusedIndex,
      labels,
      multipleChoice,
      rightAlign,
      monoColumns,
      columnRenderers,
      onSelect,
      onFocus,
      gridTemplateColumns,
      pinnedCount,
    }),
    [
      processedItemIds,
      itemsMap,
      selectedIdsSet,
      focusedIndex,
      labels,
      multipleChoice,
      rightAlign,
      monoColumns,
      columnRenderers,
      onSelect,
      onFocus,
      gridTemplateColumns,
      pinnedCount,
    ],
  );

  return (
    <div className={styles.bodyRows} style={tableWidth ? { width: tableWidth } : undefined}>
      <List<RowProps>
        listRef={listRef}
        rowProps={rowProps}
        rowComponent={({
          index,
          style,
          processedItemIds,
          itemsMap,
          selectedIdsSet,
          focusedIndex,
          labels,
          multipleChoice,
          rightAlign,
          monoColumns,
          columnRenderers,
          onSelect,
          onFocus,
          gridTemplateColumns,
          pinnedCount,
        }) => {
          const itemValue = processedItemIds[index];
          const rowData = itemsMap.get(itemValue);

          if (!rowData) return null;

          const isSelected = selectedIdsSet.has(itemValue);
          const isFocused = focusedIndex === index;
          const isPinnedSeparator = pinnedCount > 0 && index === pinnedCount - 1;

          return (
            <div style={style} className={isPinnedSeparator ? styles.pinnedSeparator : undefined}>
              <BrowserRow
                itemValue={itemValue}
                rowData={rowData}
                labels={labels}
                isSelected={isSelected}
                isFocused={isFocused}
                multipleChoice={multipleChoice}
                rightAlign={rightAlign}
                monoColumns={monoColumns}
                columnRenderers={columnRenderers}
                onSelect={onSelect}
                onFocus={onFocus}
                rowIndex={index}
                gridTemplateColumns={gridTemplateColumns}
              />
            </div>
          );
        }}
        rowCount={processedItemIds.length}
        rowHeight={ROW_HEIGHT}
        style={{ height: containerHeight, width: tableWidth, overflow: hasVerticalOverflow ? "auto" : "hidden" }}
      />
    </div>
  );
};
